import React, { useMemo, useRef, useState } from 'react';
import { BrioModal, BrioModalBody, BrioButton, BrioInput, BrioSelect } from '../../components/BrioComponents';
import { parseRatesFromXlsx } from '../utils/sampleRatesXlsx';
import { rankVersions } from '../utils/fuzzyMatch';

// Above this similarity a suggestion is auto-selected; above SAME the wording
// is treated as effectively identical (no candidate spec needed).
const MATCH_THRESHOLD = 0.30;
const SAME_THRESHOLD = 0.82;

// Import rates from a sample BOQ. Tagging (date + consultant required) is
// enforced before import. Each line is fuzzy-matched to the closest library
// item; near-but-not-identical wordings can be saved as a draft candidate
// spec under the matched item (for later reconciliation).
function ImportSampleRatesModal({ versions, vendors, findOrCreateVendor, createCandidateVersion, onCreateSource, onCreateRates, onClose, onDone }) {
  const [stage, setStage] = useState('pick'); // pick | review | importing
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [tag, setTag] = useState({ documentDate: new Date().toISOString().slice(0, 10), consultant: '', defaultVendor: '', city: '', currency: 'INR', note: '' });
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const versionById = useMemo(() => {
    const m = new Map();
    (versions || []).forEach((v) => m.set(v.id, v));
    return m;
  }, [versions]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const res = await parseRatesFromXlsx(file);
    if (res.error) { setError(res.error); return; }
    if (res.rows.length === 0) { setError('No data rows found in the sheet.'); return; }
    const prepared = res.rows.map((r) => {
      const ranked = rankVersions(r.description, versions, { limit: 8, unit: r.unit });
      const best = ranked[0];
      const match = best && best.score >= MATCH_THRESHOLD ? best.version.id : '';
      const candidate = !!(best && best.score >= MATCH_THRESHOLD && best.score < SAME_THRESHOLD);
      return { ...r, ranked, bestScore: best ? best.score : 0, match, candidate };
    });
    setFileName(file.name);
    setRows(prepared);
    setStage('review');
  };

  const setRowMatch = (rowNumber, itemVersionId) =>
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, match: itemVersionId } : r)));
  const setRowCandidate = (rowNumber, checked) =>
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, candidate: checked } : r)));

  const matchedRows = useMemo(() => rows.filter((r) => r.match), [rows]);
  const candidateCount = useMemo(() => matchedRows.filter((r) => r.candidate).length, [matchedRows]);
  const canImport = tag.documentDate && tag.consultant.trim() && matchedRows.length > 0;

  // Build the per-row match dropdown: ranked suggestions (with score) first,
  // then every other version under an "all items" group so anything is pickable.
  const renderOptions = (r) => {
    const rankedIds = new Set(r.ranked.map((s) => s.version.id));
    const rest = (versions || []).filter((v) => !rankedIds.has(v.id));
    return (
      <>
        <option value="">— skip —</option>
        {r.ranked.length > 0 && (
          <optgroup label="Suggested">
            {r.ranked.map((s) => (
              <option key={s.version.id} value={s.version.id}>
                {Math.round(s.score * 100)}% · {s.version.fullCode} · {s.version.shortDescription}
              </option>
            ))}
          </optgroup>
        )}
        <optgroup label="All items">
          {rest.map((v) => <option key={v.id} value={v.id}>{v.fullCode} · {v.shortDescription}</option>)}
        </optgroup>
      </>
    );
  };

  const handleImport = async () => {
    if (!canImport) return;
    setStage('importing');
    setError(null);

    // Vendors: default for the import + any per-row vendor names.
    let defaultVendorId = null;
    if (tag.defaultVendor.trim()) {
      const r = await findOrCreateVendor(tag.defaultVendor, { location: tag.city });
      if (!r.success) { setError(r.error); setStage('review'); return; }
      defaultVendorId = r.id;
    }
    const vendorIdByName = new Map();
    const distinctVendors = [...new Set(matchedRows.map((r) => (r.vendor || '').trim()).filter(Boolean))];
    for (const name of distinctVendors) {
      // eslint-disable-next-line no-await-in-loop
      const r = await findOrCreateVendor(name, { location: tag.city });
      if (r.success) vendorIdByName.set(name.toLowerCase(), r.id);
    }

    const srcRes = await onCreateSource({
      filename: fileName, documentDate: tag.documentDate, consultant: tag.consultant,
      defaultVendorId, city: tag.city, currency: tag.currency, note: tag.note,
    });
    if (!srcRes.success) { setError(srcRes.error); setStage('review'); return; }
    const sourceDocumentId = srcRes.source.id;

    // Resolve each matched row to a target item_version, creating candidate
    // draft versions where requested.
    const rateRows = [];
    for (const r of matchedRows) {
      const matched = versionById.get(r.match);
      let targetVersionId = r.match;
      if (r.candidate && matched) {
        // eslint-disable-next-line no-await-in-loop
        const cv = await createCandidateVersion(matched.itemCode, {
          shortDescription: r.description,
          unit: r.unit || matched.unit,
          sourceDocumentId,
          changeReason: `Imported candidate from ${fileName}`,
        });
        if (!cv.success) { setError(cv.error); setStage('review'); return; }
        targetVersionId = cv.version.id;
      }
      rateRows.push({
        itemVersionId: targetVersionId,
        vendorId: (r.vendor && vendorIdByName.get(r.vendor.trim().toLowerCase())) || defaultVendorId,
        rateQuoted: r.rateQuoted,
        rateBilled: r.rateBilled,
        city: tag.city,
        rateDate: tag.documentDate,
        contractor: r.vendor || null,
        source: 'import',
        sourceFilename: fileName,
        sourceConsultant: tag.consultant,
        sourceDocumentId,
      });
    }

    const res = await onCreateRates(rateRows);
    if (!res.success) { setError(res.error); setStage('review'); return; }
    onDone?.();
    onClose();
  };

  const labelStyle = { fontSize: '0.78rem', color: 'var(--brio-text-muted)', display: 'block' };
  const skipped = rows.length - matchedRows.length;

  const footer = stage === 'pick'
    ? <BrioButton variant="secondary" onClick={onClose}>Cancel</BrioButton>
    : (
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={stage === 'importing'}>Cancel</BrioButton>
        <BrioButton variant="primary" onClick={handleImport} disabled={!canImport || stage === 'importing'}>
          {stage === 'importing' ? 'Importing…' : `Import ${matchedRows.length} rate${matchedRows.length === 1 ? '' : 's'}${candidateCount ? ` · ${candidateCount} candidate spec${candidateCount === 1 ? '' : 's'}` : ''}${skipped ? ` · skip ${skipped}` : ''}`}
        </BrioButton>
      </>
    );

  return (
    <BrioModal title="Import rates from sample BOQ" onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {error && <div style={{ padding: '0.6rem 0.9rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '0.9rem' }}>{error}</div>}

        {stage === 'pick' && (
          <div>
            <p style={{ marginTop: 0 }}>
              Choose an <code>.xlsx</code> with a <strong>Description</strong> column and a <strong>Rate</strong> column
              (optionally Code, Unit, Rate tender, Vendor). Each line is matched to the closest library item; you'll
              tag the source and confirm matches next.
            </p>
            <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} style={{ display: 'none' }} />
            <BrioButton variant="primary" onClick={() => fileRef.current?.click()}>Choose Excel file…</BrioButton>
          </div>
        )}

        {(stage === 'review' || stage === 'importing') && (
          <div>
            <div style={{ border: '1px solid var(--brio-gold, #9F902A)', borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', background: '#fffdf5' }}>
              <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Source: <code>{fileName}</code> — tag it so every rate is traceable (<strong>date</strong> and <strong>consultant</strong> required).
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.6rem' }}>
                <label style={labelStyle}>BOQ date *
                  <BrioInput type="date" value={tag.documentDate} onChange={(e) => setTag({ ...tag, documentDate: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label style={labelStyle}>Consultant *
                  <BrioInput value={tag.consultant} onChange={(e) => setTag({ ...tag, consultant: e.target.value })} placeholder="e.g. ABC Cost Consultants" style={{ width: '100%' }} />
                </label>
                <label style={labelStyle}>Default vendor
                  <input list="vendor-list" value={tag.defaultVendor} onChange={(e) => setTag({ ...tag, defaultVendor: e.target.value })} placeholder="type to pick or create"
                    style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                  <datalist id="vendor-list">{(vendors || []).map((v) => <option key={v.id} value={v.name} />)}</datalist>
                </label>
                <label style={labelStyle}>City
                  <BrioInput value={tag.city} onChange={(e) => setTag({ ...tag, city: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label style={labelStyle}>Currency
                  <BrioSelect value={tag.currency} onChange={(e) => setTag({ ...tag, currency: e.target.value })} style={{ width: '100%' }}>
                    <option value="INR">INR</option><option value="EUR">EUR</option>
                  </BrioSelect>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem' }}>
              <strong>{rows.length}</strong> rows · <span style={{ color: '#2e7d32' }}>{matchedRows.length} matched</span>
              {candidateCount > 0 && <span> · {candidateCount} saved as candidate spec</span>}
              {skipped > 0 && <span>, <span style={{ color: '#a94442' }}>{skipped} unmatched</span></span>}.
              <span style={{ color: 'var(--brio-text-muted)' }}> Tick <em>candidate</em> to keep the imported wording as a draft variant under the matched item.</span>
            </div>
            <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Imported description</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Unit</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Quoted</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Tender</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Vendor</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Match to library item</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>Candidate</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.rowNumber} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', background: r.match ? 'transparent' : '#fff8f8' }}>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{r.description || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{r.unit || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{r.rateQuoted ?? '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{r.rateBilled ?? '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{r.vendor || '—'}</td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>
                        <BrioSelect value={r.match} onChange={(e) => setRowMatch(r.rowNumber, e.target.value)} style={{ fontSize: '0.78rem', minWidth: 230 }}>
                          {renderOptions(r)}
                        </BrioSelect>
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center' }}>
                        <input type="checkbox" checked={r.candidate} disabled={!r.match} onChange={(e) => setRowCandidate(r.rowNumber, e.target.checked)}
                          title="Save the imported wording as a draft candidate spec under the matched item" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default ImportSampleRatesModal;
